import React from 'react'
import { usePlatforms } from '../../contexts/PlatformsContext'
import './style/platformTabs.scss'

/**
 * The platform tabs (Meta Quest / Steam / Apple Vision) shown at the top of Genre
 * Explorer, Publisher Explorer, Keyword Explorer, and (as a multi-select filter,
 * see `selected`/`onToggle` below) the dashboard's "Most Tracked Games" section.
 * Used to be independently hand-rolled in every one of those places — one copy
 * dropped the platform icon entirely, others duplicated the same CSS as an inline
 * `<style>` block. This is the one shared version all of them use.
 *
 * Two modes, picked by which props are passed:
 *  - Single-select (`activeTab` + `onChange`): exactly one platform highlighted
 *    at a time — used for Keyword/Genre/Publisher Explorer and Ad Discovery.
 *    Pass `variant="pill"` (the standard look for these) for bordered, rounded
 *    chips; the plain underline is the legacy look, kept as the default only
 *    for backwards compatibility with any caller that doesn't pass `variant`.
 *    Ad Discovery's ad-creative search only has data for Meta today, so it
 *    passes `disabledPlatforms={['steam', 'apple_vision', 'pico']}` rather than a
 *    fixed `platforms` override — the tabs still show every store platform,
 *    matching the shared UI, but the unsupported ones are disabled.
 *  - Multi-select (`selected` — an array — + `onToggle`): a row of independently
 *    toggleable pill chips, for filters where more than one platform can be active
 *    at once (e.g. the dashboard's "Most Tracked Games" filter). Always use
 *    `variant="pill"` here — an underline reads oddly once several items are
 *    highlighted simultaneously.
 *
 * By default the list of platforms to render comes from `PlatformsContext`
 * (whatever the `/tracker/platforms` API returns). Pass `platforms` to render
 * a fixed, caller-supplied list instead, for a screen whose tabs mean
 * something other than the store-platform list (e.g. an ads-source picker).
 *
 * Pass `hiddenPlatforms` (an array of platform ids, e.g. `['apple_vision']`)
 * to drop specific platforms from the row entirely — everything else still
 * renders normally. Useful when a screen only ever wants a subset of the
 * platforms the API/`platforms` override would otherwise offer.
 *
 * Pass `disabledPlatforms` (an array of platform ids) to keep specific
 * platforms visible but non-interactive — e.g. a platform whose backend
 * data isn't available yet. Disabled tabs can never become active/selected
 * (clicks are ignored) and show a "Coming soon" tooltip on hover/click.
 * Unlike `hiddenPlatforms`, the tab stays in the row so the UI still
 * communicates every platform the app supports.
 */
export default function PlatformTabs({
  activeTab,
  onChange,
  selected,
  onToggle,
  disabled = false,
  showDivider = true,
  variant = 'underline',
  className = '',
  platforms,
  hiddenPlatforms,
  disabledPlatforms,
  disabledTooltip = 'Coming soon',
}) {
  const { getActivePlatforms, loading } = usePlatforms()
  const isMulti = Array.isArray(selected)
  const isPill = variant === 'pill'
  const hasOverride = Array.isArray(platforms)
  const hiddenSet = Array.isArray(hiddenPlatforms) ? new Set(hiddenPlatforms) : null
  const disabledSet = Array.isArray(disabledPlatforms) ? new Set(disabledPlatforms) : null

  if (!hasOverride && loading) {
    return <div className="platform-tabs-loading">Loading platforms...</div>
  }

  const visiblePlatforms = (hasOverride ? platforms : getActivePlatforms()).filter(
    (platform) => !hiddenSet || !hiddenSet.has(platform.id),
  )

  return (
    <div className={`platform-tabs-root ${className}`.trim()}>
      <div className={`platform-tabs-row${isPill ? ' platform-tabs-row--pill' : ''}`}>
        {visiblePlatforms.map((platform) => {
          const isActive = isMulti ? selected.includes(platform.id) : activeTab === platform.id
          const isPlatformDisabled = disabled || Boolean(disabledSet?.has(platform.id))
          const button = (
            <button
              key={platform.id}
              type="button"
              aria-pressed={isMulti ? isActive : undefined}
              aria-disabled={isPlatformDisabled}
              className={`platform-tab-btn${isPill ? ' platform-tab-btn--pill' : ''}${isActive ? ' active' : ''}${isPlatformDisabled ? ' platform-tab-btn--disabled' : ''}`}
              onClick={() => {
                if (isPlatformDisabled) return
                isMulti ? onToggle?.(platform.id) : onChange?.(platform.id)
              }}
            >
              {platform.icon_url && (
                <img src={platform.icon_url} alt={platform.display_name} className="platform-tab-icon" />
              )}
              {platform.display_name}
            </button>
          )

          if (!isPlatformDisabled) return button

          return (
            <span
              key={platform.id}
              className="platform-tab-tooltip-wrap"
              data-tooltip={disabledTooltip}
            >
              {button}
            </span>
          )
        })}
      </div>
      {showDivider ? <div className={`platform-tabs-divider${isPill ? ' platform-tabs-divider--pill' : ''}`} /> : null}
    </div>
  )
}
